import * as React from 'react';

import { useRoleId } from '@/app/hooks/useAuth/useRoleId';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useDialogFormStore } from '@/components/dialog/form.store';

import { useCan } from '@/components/can/Can.tsx';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDropdown } from '@/hooks/useDropdown';
import { useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { ROLESTABLEDATA } from '@/services/rolesPrivilege/roles.types';
import { AddRoleForm } from '../AddRoleForm';
import { getColumns } from './Column';

export function RolesTable({
  data,
  isLoading,
  pagination,
}: {
  data: ROLESTABLEDATA[];
  isLoading: boolean;
  pagination: Pagination;
}) {
  const roleId = useRoleId();
  const { t } = useDynamicLocale();
  const columns = React.useMemo(() => getColumns(roleId, t), [roleId, t]);
  const { onOpen } = useDialogFormStore();
  const { data: userRoleType = [] } = useDropdown('role');
  const canAddNew = useCan({ allOf: [PERMS.role.type.create] });
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );
  const onAddNew = () => {
    onOpen({
      title: 'Add New Commission Category',
      okText: t('common.page.modal.submit'),
      size: 'lg',
      modalTitle: 'rolesPrivileges.addRoleModal.title',
      component: <AddRoleForm userRoleType={userRoleType} />,
    });
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pageCount,
    initialState: {
      sorting: [{ id: 'roleId', desc: true }],
    },
    meta: {
      title: t('userManagement.rolesAndPrivileges.table.title'),
      actionKey: canAddNew ? 'onAddNew' : undefined,
      action: canAddNew ? onAddNew : null,
      loading: isLoading,
      totalPage: pagination.totalItems,
    },
    getRowId: (originalRow) => String(originalRow.roleId),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table} className="rounded-t-none rounded-b-xl">
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
